import React from 'react';
import TextField from "@material-ui/core/TextField";
import {TextareaAutosize} from "@material-ui/core";
import cloneDeep from 'lodash/cloneDeep';
import './VideoForm.scss';
import isNumeric from "../../util/isNumeric";
import MultipleValuesInput from "../MultipleValuesInput/MultipleValuesInput";
import {KeyboardDatePicker} from "@material-ui/pickers";

const defaultForm = {
  name: {error: null, value: ''},
  // A Date, an Invalid Date while someone is still typing, or null. The submitting
  // container turns it into the "YYYY-MM-DD" string the API stores.
  recordedDate: {error: null, value: null},
  // Only reaches the API for rows that have no date. Everything dated derives its year
  // from the date server-side, so the two can never disagree.
  year: {error: null, value: (new Date()).getFullYear()},
  description: {error: null, value: ''},
  people: {error: null, value: []},
  places: {error: null, value: []},
  categories: {error: null, value: []},
  tags: {error: null, value: []},
};

export function getDefaultForm() {
  return cloneDeep(defaultForm);
}

export function recordedDateIsValid(value) {
  return value instanceof Date && !isNaN(value.getTime())
    && value.getFullYear() > 1970 && value <= new Date();
}

/**
 * `allowYearOnly` is what separates creating from editing. New material always gets a
 * real date. Items imported before this field existed only ever knew their year, so
 * their edit form keeps the year box and leaves the date optional -- nobody should have
 * to invent a day for a 1977 Super8 transfer just to fix a typo in its tags.
 */
const VideoOrGalleryDetails = ({nameFieldTitle, fieldsDisabled, form, setForm, allowYearOnly = false}) => {

  function updateForm(name, value) {
    setForm((prevState) => {
      return {...prevState, [name]: {...prevState[name], value: value}};
    });
  }

  function nameIsValid(value) {
    return value.trim().length > 2;
  }

  function yearIsValid(value) {
    return isNumeric(value) && parseInt(value) > 1970 && parseInt(value) <= (new Date()).getFullYear();
  }

  function validateField(name, value) {
    let error = null;
    if (name === 'name') {
      error = !nameIsValid(value);
    } else if (name === 'year') {
      error = !yearIsValid(value);
    } else if (name === 'recordedDate') {
      error = (value === null || value === undefined)
        ? !allowYearOnly
        : !recordedDateIsValid(value);
    }
    setForm((prevState) => {
      return {...prevState, [name]: {...form[name], error: error}};
    });
  }

  return (
    <div className="VideoForm">
      <div className="VideoForm__field">
        <TextField
          fullWidth
          required
          error={form.name.error}
          helperText={''}
          id="name"
          label={nameFieldTitle}
          variant="outlined"
          color="secondary"
          value={form.name.value}
          disabled={fieldsDisabled}
          onInput={(e) => {updateForm('name', e.target.value);}}
          onChange={(e) => {updateForm('name', e.target.value);}}
          onBlur={(e) => {validateField('name', e.target.value);}}
        />
      </div>
      <div className="VideoForm__field">
        <KeyboardDatePicker
          fullWidth
          required={!allowYearOnly}
          clearable={allowYearOnly}
          error={form.recordedDate.error}
          helperText={allowYearOnly ? 'Optional. Ohne Datum wird nach dem Jahr sortiert.' : ''}
          id="recordedDate"
          label="Datum"
          inputVariant="outlined"
          color="secondary"
          format="dd.MM.yyyy"
          placeholder="TT.MM.JJJJ"
          // The archive reaches back to the seventies, so the calendar opens on the year
          // list instead of on the current month.
          openTo="year"
          views={['year', 'month', 'date']}
          minDate={new Date(1971, 0, 1)}
          maxDate={new Date()}
          value={form.recordedDate.value}
          disabled={fieldsDisabled}
          onChange={(date) => {updateForm('recordedDate', date);}}
          onBlur={() => {validateField('recordedDate', form.recordedDate.value);}}
          invalidDateMessage="Ungültiges Datum"
          minDateMessage="Datum liegt zu weit zurück"
          maxDateMessage="Datum liegt in der Zukunft"
          cancelLabel="Abbrechen"
          clearLabel="Löschen"
          okLabel="Übernehmen"
        />
      </div>
      {allowYearOnly && form.recordedDate.value === null ? (
        <div className="VideoForm__field">
          <TextField
            fullWidth
            required
            error={form.year.error}
            helperText={''}
            id="year"
            label="Jahr"
            variant="outlined"
            color="secondary"
            type="number"
            InputProps={{ inputProps: { min: 1970, max: (new Date().getFullYear()), step: 1 } }}
            value={form.year.value}
            disabled={fieldsDisabled}
            onInput={(e) => {updateForm('year', e.target.value);}}
            onChange={(e) => {updateForm('year', e.target.value);}}
            onBlur={(e) => {validateField('year', e.target.value);}}
          />
        </div>
      ) : null}
      <div className="VideoForm__field">
        <div style={{ border: '1px solid rgba(0, 0, 0, 0.23)', padding: '18.5px 14px', borderRadius: '4px'}}>
          <TextareaAutosize
            className="MuiInputBase-input MuiOutlinedInput-input"
            minRows={3}
            style={{width: '100%', padding: 0}}
            placeholder="Beschreibung"
            value={form.description.value}
            disabled={fieldsDisabled}
            onChange={(e) => {updateForm('description', e.target.value);}}
          />
        </div>
      </div>

      <div className="VideoForm__field">
        <MultipleValuesInput freeFormLabel="Nicht aufgeführte Person eingeben" allowFreeform={true} useAvatars={true} autocompleteValues={['Omi', 'Opi', 'Gine', 'Claudi', 'Hebbie', 'Jan', 'Fabian', 'Jonas', 'Food', 'Milena', 'Steve', 'Pierre']} values={form.people.value} label="Personen" onUpdateValues={(value) => {updateForm('people', value)}} />
      </div>

      <div className="VideoForm__field">
        <MultipleValuesInput freeFormLabel="Nicht aufgeführten Ort eingeben" allowFreeform={true} useAvatars={false} autocompleteValues={['Heubach', 'Bubsheim', 'Gengenbach', 'Freiburg', 'Münster', 'Berlin', 'Reichenbach']} values={form.places.value} label="Orte" onUpdateValues={(value) => {updateForm('places', value)}} />
      </div>

      <div className="VideoForm__field">
        <MultipleValuesInput allowFreeform={false} useAvatars={false} autocompleteValues={['Familienleben', 'Urlaub', 'Geburtstag', 'Weihnachten', 'Veranstaltungen', 'Ausflug', 'Ostern', 'Familienfeste']} values={form.categories.value} label="Kategorien" onUpdateValues={(value) => {updateForm('categories', value)}} />
      </div>

      <div className="VideoForm__field">
        <MultipleValuesInput freeFormLabel="Neues Stichwort/Tag eingeben" allowFreeform={true} useAvatars={false} autocompleteValues={[]} values={form.tags.value} label="Tags/Stichworte" onUpdateValues={(value) => {updateForm('tags', value)}} />
      </div>
    </div>
  );
}

export default VideoOrGalleryDetails;
